import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actions as advActions } from "../components/advertising/logic/formActions";
import { Paths } from "../components/common/navigation";
import { currentUserIdSelector } from "../components/login/logic/actions";
import { createDatesFromTimeFrame } from "../core/date";
import envconfig from "../envconfig";
import { notifyUser } from "../notification";
import filmReleaseService from "../services/filmReleaseService";
import { oneDay } from "../utils/constants";

export const buildUnreportedCountMessage = (count) => {
    const getUnreportedAdvMessage = `There ${count > 1 ? "are" : "is"} ${count} ${
        count > 1 ? "advertisements" : "advertisement"
    } that have not been reported on. Would you like to review the outstanding items?`;

    return getUnreportedAdvMessage;
};

export function getUnreportedCount(userId) {
    const { futureDate: futureDate, pastDate: pastDate } = createDatesFromTimeFrame(
        envconfig.PERIOD_CHECK_UNREPORTED_IN_DAYS
    );

    const params = {
        userId: userId,
        dateFrom: pastDate.toISOString().split("T")[0],
        dateTill: futureDate.toISOString().split("T")[0],
    };
    return filmReleaseService.getUnreportedCount(params);
}

const useNotificationAtFive = (modalCallback) => {
    const userId = useSelector(currentUserIdSelector);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const onClickNotification = useCallback(() => {
        const { futureDate: tomorrow, pastDate: yesterday } = createDatesFromTimeFrame(
            envconfig.PERIOD_CHECK_UNREPORTED_IN_DAYS
        );
        const params = [
            {
                attribute: "advReported",
                value: false,
            },
            {
                attribute: "advEnteredDateStart",
                value: yesterday.toISOString(),
            },
            {
                attribute: "advEnteredDateEnd",
                value: tomorrow.toISOString(),
            },
        ];

        dispatch(advActions.CHANGE_FORM.send(params));
        navigate(Paths.ADVERTISING);
    }, [dispatch, navigate]);

    const checkUnreported = useCallback(
        async (type = "web-notification") => {
            const response = await getUnreportedCount(userId);
            if (response > 0) {
                const message = buildUnreportedCountMessage(response);
                if (type === "web-notification") {
                    notifyUser(message, {
                        onClick: () => {
                            onClickNotification();
                        },
                    });
                } else if (type === "alert") {
                    modalCallback(message);
                }
            }
        },
        [modalCallback, onClickNotification, userId]
    );

    useEffect(() => {
        if (userId) {
            const twentyFourHoursMs = oneDay;
            const now = new Date();
            const hour = envconfig.HOUR_TO_NOTIFY_UNREPORTED_AD;
            const minutes = envconfig.MIN_TO_NOTIFY_UNREPORTED_AD;
            let eta_ms =
                new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minutes, 0, 0).getTime() - now;
            if (eta_ms < 0) {
                eta_ms += twentyFourHoursMs;
            }
            let intervalId = null;
            const timeOutId = setTimeout(function () {
                checkUnreported("web-notification");

                intervalId = setInterval(() => checkUnreported("web-notification"), twentyFourHoursMs);
            }, eta_ms);

            return () => {
                clearInterval(intervalId);
                clearTimeout(timeOutId);
            };
        }
    }, [checkUnreported, userId]);

    useEffect(() => {
        if (userId) {
            const twentyFourHoursMs = oneDay;
            const now = new Date();
            const hour = envconfig.HOUR_TO_NOTIFY_UNREPORTED_AD;
            const minutes = envconfig.MIN_TO_NOTIFY_UNREPORTED_AD + envconfig.MIN_TO_DOBLE_NOTIFY_UNREPORTED_AD;
            let eta_ms =
                new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minutes, 0, 0).getTime() -
                now.getTime();
            if (eta_ms < 0) {
                eta_ms += twentyFourHoursMs;
            }
            let intervalId = null;
            const timeOutId = setTimeout(function () {
                checkUnreported("alert");

                intervalId = setInterval(checkUnreported, twentyFourHoursMs);
            }, eta_ms);

            return () => {
                clearInterval(intervalId);
                clearTimeout(timeOutId);
            };
        }
    }, [checkUnreported, userId]);

    return {
        onClickNotification,
    };
};

export default useNotificationAtFive;
